<template>
  <b-modal v-model="visible" size="xl" centered hide-header hide-footer body-class="p-0" content-class="crgm-content">
    <!-- Hero -->
    <div class="crgm__hero">
      <div class="crgm__hero-icon"><i class="fa-solid fa-scale-balanced"></i></div>
      <div class="crgm__hero-text">
        <h4 class="mb-0">{{ details.affiliateName || affiliateLabel }}</h4>
        <small>{{ $t('affiliateDashboard.crgModal.subtitle') }}</small>
      </div>
      <button v-if="canExport" type="button" class="crgm__excel" :disabled="exporting"
              v-b-tooltip.hover :title="$t('operations.export')" @click="exportExcel">
        <b-spinner v-if="exporting" small/>
        <i v-else class="fa-regular fa-file-excel"></i>
      </button>
      <button type="button" class="crgm__close" @click="visible = false"><i class="fa-solid fa-xmark"></i></button>
    </div>

    <div class="crgm__body">
      <b-overlay :show="loading" rounded opacity="0.4" spinner-variant="primary" style="min-height: 220px">
        <template v-if="!loading">
          <!-- Contract chips -->
          <div class="crgm__contract">
            <span class="crgm-chip crgm-chip--type">
              <i class="fa-solid fa-file-signature mr-25"></i>
              {{ details.cpaType === 'FLAT' ? $t('affiliates.cpaTypeFlat') : $t('affiliates.cpaTypeCrg') }}
            </span>
            <span class="crgm-chip"><i class="fa-solid fa-money-bill-wave mr-25"></i>CPA {{ convertToEURO(details.cpaPrice || 0) }}</span>
            <span v-if="details.crgPercent != null" class="crgm-chip"><i class="fa-solid fa-percent mr-25"></i>CRG {{ details.crgPercent }}%</span>
            <span class="crgm-chip"><i class="fa-solid fa-calendar-week mr-25"></i>{{ $t('affiliates.cpaPeriodFixed') }}</span>
            <span v-if="details.contractStart" class="crgm-chip">
              <i class="fa-solid fa-bolt mr-25"></i>{{ $t('affiliateDashboard.crgModal.activeSince') }} {{ details.contractStart }}
            </span>
          </div>

          <div v-if="details.cpaType === 'FLAT'" class="crgm__flat">
            <i class="fa-solid fa-circle-info mr-50"></i>{{ $t('affiliateDashboard.crgModal.flatNote') }}
          </div>

          <template v-else-if="details.weeks">
            <!-- Totals tiles -->
            <div class="crgm__totals">
              <div class="crgm-tile">
                <span class="crgm-tile__value">{{ details.totalLeads || 0 }}</span>
                <span class="crgm-tile__label">{{ $t('columns.leads') }}</span>
              </div>
              <div class="crgm-tile">
                <span class="crgm-tile__value">{{ details.guaranteedFtds || 0 }}</span>
                <span class="crgm-tile__label">{{ $t('affiliateDashboard.crgModal.minGuaranteed') }}</span>
              </div>
              <div class="crgm-tile">
                <span class="crgm-tile__value">{{ details.inWindowFtds || 0 }}</span>
                <span class="crgm-tile__label">{{ $t('affiliateDashboard.crgModal.inWindow') }}</span>
              </div>
              <div class="crgm-tile crgm-tile--free">
                <span class="crgm-tile__value">{{ details.freeLateFtds || 0 }}</span>
                <span class="crgm-tile__label">{{ $t('affiliateDashboard.crgModal.freeLate') }}</span>
              </div>
              <div class="crgm-tile crgm-tile--late">
                <span class="crgm-tile__value">{{ details.outsideWindowFtds || 0 }}</span>
                <span class="crgm-tile__label">{{ $t('affiliateDashboard.crgModal.tooLate') }}</span>
              </div>
              <div class="crgm-tile" :class="details.toMinimum > 0 ? 'crgm-tile--warn' : 'crgm-tile--ok'">
                <span class="crgm-tile__value">{{ details.toMinimum > 0 ? details.toMinimum : '+' + (details.overMinimum || 0) }}</span>
                <span class="crgm-tile__label">{{ details.toMinimum > 0 ? $t('affiliateDashboard.crgModal.stillNeeded') : $t('affiliateDashboard.crgModal.overMin') }}</span>
              </div>
              <div class="crgm-tile crgm-tile--paid">
                <span class="crgm-tile__value">{{ details.paidFtds || 0 }}</span>
                <span class="crgm-tile__label">{{ $t('affiliateDashboard.crgModal.paidFtds') }}</span>
              </div>
            </div>

            <!-- Payment breakdown: real FTDs paid at CPA + missing FTDs still
                 paid because of the guarantee (e.g. 5 of 7 → 5 FTD + 2 CRG). -->
            <div class="crgm__paysplit">
              <div class="crgm-pay crgm-pay--ftd">
                <span class="crgm-pay__label"><i class="fa-solid fa-user-check mr-25"></i>{{ $t('affiliateDashboard.crgModal.payFtds') }}</span>
                <span class="crgm-pay__math">{{ realPaidCount }} × {{ convertToEURO(details.cpaPrice || 0) }}</span>
                <span class="crgm-pay__value">{{ convertToEURO(ftdCost) }}</span>
              </div>
              <span class="crgm-pay__op">+</span>
              <div class="crgm-pay crgm-pay--cg">
                <span class="crgm-pay__label"><i class="fa-solid fa-shield-halved mr-25"></i>{{ $t('affiliateDashboard.crgModal.payGuarantee') }}</span>
                <span class="crgm-pay__math">{{ guaranteeFillCount }} × {{ convertToEURO(details.cpaPrice || 0) }}</span>
                <span class="crgm-pay__value">{{ convertToEURO(guaranteeCost) }}</span>
              </div>
              <span class="crgm-pay__op">=</span>
              <div class="crgm-pay crgm-pay--total">
                <span class="crgm-pay__label"><i class="fa-solid fa-coins mr-25"></i>{{ $t('affiliateDashboard.crgModal.payTotal') }}</span>
                <span class="crgm-pay__math">{{ details.paidFtds || 0 }} × {{ convertToEURO(details.cpaPrice || 0) }}</span>
                <span class="crgm-pay__value">{{ details.cpaCost != null ? convertToEURO(details.cpaCost) : convertToEURO(totalCost) }}</span>
              </div>
            </div>

            <!-- Weekly breakdown -->
            <div class="crgm__table-wrap">
              <b-table small hover responsive :items="details.weeks || []" :fields="weekFields"
                       show-empty class="crgm-table mb-0">
                <template #cell(weekStart)="d">
                  <span class="font-weight-bold text-nowrap">{{ d.value }} → {{ d.item.weekEnd }}</span>
                  <small class="d-block text-muted">{{ $t('affiliateDashboard.crgModal.windowUntil') }} {{ d.item.windowEnd }}</small>
                </template>
                <template #cell(guarantee)="d"><span class="font-weight-bold">{{ d.value }}</span></template>
                <template #cell(lateFtds)="d">
                  {{ d.value }}
                  <b-badge v-if="d.item.freeLate" variant="light-info" class="ml-25">{{ d.item.freeLate }} {{ $t('affiliateDashboard.crgModal.free') }}</b-badge>
                </template>
                <template #cell(outsideWindowFtds)="d">
                  <span :class="d.value ? 'text-danger font-weight-bold' : 'text-muted'">{{ d.value || '—' }}</span>
                </template>
                <template #cell(paid)="d"><span class="font-weight-bold">{{ d.value }}</span></template>
                <!-- What this week actually costs: paid FTDs × CPA price,
                     split into real FTDs + guarantee fill when they differ -->
                <template #cell(payment)="d">
                  <span class="crgm__payment">{{ convertToEURO((d.item.paid || 0) * (details.cpaPrice || 0)) }}</span>
                  <small v-if="weekGuaranteeFill(d.item) > 0" class="d-block crgm__payment-split">
                    {{ d.item.inWindow || 0 }} FTD + {{ weekGuaranteeFill(d.item) }} CRG
                  </small>
                </template>
                <!-- Same 4 states as the dashboard cell: a CLOSED week below its
                     minimum is "closed short", never "min reached". -->
                <template #cell(status)="d">
                  <b-badge v-if="d.item.windowOpen && d.item.toMinimum > 0" variant="light-warning">
                    {{ $t('affiliateDashboard.crgNeedsMore', { count: d.item.toMinimum }) }}
                  </b-badge>
                  <b-badge v-else-if="d.item.overMinimum > 0" variant="light-success">
                    {{ $t('affiliateDashboard.crgExtraPaid', { count: d.item.overMinimum }) }}
                  </b-badge>
                  <b-badge v-else-if="d.item.inWindow < d.item.guarantee" variant="light-danger">
                    {{ $t('affiliateDashboard.crgClosedShort', { count: d.item.guarantee - d.item.inWindow }) }}
                  </b-badge>
                  <b-badge v-else-if="d.item.windowOpen" variant="light-primary">{{ $t('affiliateDashboard.crgModal.open') }}</b-badge>
                  <b-badge v-else variant="light-secondary">{{ $t('affiliateDashboard.crgMet') }}</b-badge>
                </template>
                <template #empty><div class="text-center text-muted py-2">{{ $t('mobileDash.noData') }}</div></template>
              </b-table>
            </div>

            <!-- The rule, spelled out -->
            <div class="crgm__rule">
              <i class="fa-solid fa-circle-info mr-50"></i>{{ $t('affiliates.cpaCrgRule') }}
            </div>
          </template>

          <div v-else class="crgm__flat">
            <i class="fa-solid fa-circle-info mr-50"></i>{{ $t('affiliateDashboard.crgModal.noContract') }}
          </div>
        </template>
      </b-overlay>
    </div>
  </b-modal>
</template>

<script>
import { mapActions } from 'vuex'
import mixinBase from '@/components/mixin/mixinBase'
import mixinNotifications from '@/components/mixin/mixinNotifications'
import mixinPermissions from '@/components/mixin/mixinPermissions'

/**
 * CRG drill modal: everything behind an affiliate's CRG columns — the active
 * contract, totals, and the week-by-week settlement (guarantee vs. FTDs,
 * free late FTDs, too-late FTDs, extras, cost).
 */
export default {
  name: 'AffiliateCrgModal',
  mixins: [mixinBase, mixinNotifications, mixinPermissions],
  data: () => ({
    visible: false,
    loading: false,
    exporting: false,
    affiliateLabel: '',
    details: {},
    // Kept from show() so the Excel export reuses the exact same query.
    query: null,
  }),
  computed: {
    canExport() {
      return this.hasPermission(this.permissions.EXPORT_EXCEL)
          && this.query != null
          && (this.details.weeks || []).length > 0
    },
    // Payment split: paid = max(guarantee, FTDs in window), so the real FTDs
    // are paid at CPA and the remainder is the guarantee fill ("paid as CRG").
    realPaidCount() {
      return this.details.inWindowFtds || 0
    },
    guaranteeFillCount() {
      return Math.max(0, (this.details.paidFtds || 0) - (this.details.inWindowFtds || 0))
    },
    ftdCost() {
      return this.realPaidCount * (this.details.cpaPrice || 0)
    },
    guaranteeCost() {
      return this.guaranteeFillCount * (this.details.cpaPrice || 0)
    },
    totalCost() {
      return (this.details.paidFtds || 0) * (this.details.cpaPrice || 0)
    },
    weekFields() {
      return [
        { key: 'weekStart', label: this.$t('affiliateDashboard.crgModal.week') },
        { key: 'leads', label: this.$t('columns.leads') },
        { key: 'guarantee', label: this.$t('affiliateDashboard.crgModal.minGuaranteed') },
        { key: 'ftdsInWeek', label: this.$t('affiliateDashboard.crgModal.ftdsInWeek') },
        { key: 'lateFtds', label: this.$t('affiliateDashboard.crgModal.lateFtds') },
        { key: 'outsideWindowFtds', label: this.$t('affiliateDashboard.crgModal.tooLate') },
        { key: 'paid', label: this.$t('affiliateDashboard.crgModal.paidFtds') },
        { key: 'payment', label: this.$t('affiliateDashboard.crgModal.weekPayment') },
        { key: 'status', label: this.$t('affiliateDashboard.crgStatus') },
      ]
    },
  },
  methods: {
    ...mapActions('dashboard', ['getAffiliateCrgDetails', 'downloadAffiliateCrgDetails']),
    async exportExcel() {
      if (this.query == null) return
      this.exporting = true
      try {
        await this.downloadAffiliateCrgDetails(this.query)
      } catch (err) {
        this.showErrorNotification(
            this.$t('globalMessages.fetchDataError', { system: this.$t('menu.affiliateDashboard') }),
            this.getErrorDescription(err))
      } finally {
        this.exporting = false
      }
    },
    // Per-week guarantee fill: how many of the week's paid FTDs are NOT real
    // deliveries but the guaranteed minimum (paid without an FTD behind them).
    weekGuaranteeFill(week) {
      return Math.max(0, (week.paid || 0) - (week.inWindow || 0))
    },
    show({ affiliateId, label, filter }) {
      this.affiliateLabel = label || ''
      this.details = {}
      this.query = { affiliateId, filter }
      this.visible = true
      this.loading = true
      this.getAffiliateCrgDetails({ affiliateId, filter })
          .then(details => {
            this.details = details || {}
          })
          .catch(err => {
            this.showErrorNotification(
                this.$t('globalMessages.fetchDataError', { system: this.$t('menu.affiliateDashboard') }),
                this.getErrorDescription(err))
            this.visible = false
          })
          .finally(() => {
            this.loading = false
          })
    },
  },
}
</script>

<style lang="scss">
.crgm-content {
  border: none;
  border-radius: .8rem;
  overflow: hidden;
}
.crgm__hero {
  display: flex;
  align-items: center;
  gap: .9rem;
  padding: 1.1rem 1.4rem;
  color: #fff;
  background: linear-gradient(120deg, #1f2a44, #3b5b92);
}
.crgm__hero-icon {
  width: 44px;
  height: 44px;
  border-radius: .65rem;
  background: rgba(255, 255, 255, .18);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1rem;
  flex-shrink: 0;
}
.crgm__hero-text { flex: 1; min-width: 0; h4 { color: #fff; font-weight: 700; } small { opacity: .9; } }
.crgm__close {
  border: none;
  background: rgba(255, 255, 255, .15);
  color: #fff;
  width: 32px;
  height: 32px;
  border-radius: 10px;
  cursor: pointer;
  flex-shrink: 0;
  transition: background .15s ease, transform .2s ease;
  &:hover { background: rgba(255, 255, 255, .3); transform: rotate(90deg); }
}
.crgm__excel {
  border: none;
  background: rgba(40, 199, 111, .25);
  color: #fff;
  width: 32px;
  height: 32px;
  border-radius: 10px;
  cursor: pointer;
  flex-shrink: 0;
  margin-right: .35rem;
  transition: background .15s ease;
  &:hover:not(:disabled) { background: rgba(40, 199, 111, .45); }
  &:disabled { opacity: .6; cursor: default; }
}

.crgm__body { padding: 1.1rem 1.4rem 1.3rem; }

.crgm__contract { display: flex; flex-wrap: wrap; gap: .45rem; margin-bottom: 1rem; }
.crgm-chip {
  display: inline-flex;
  align-items: center;
  padding: .3rem .75rem;
  border-radius: 999px;
  background: rgba(0, 123, 255, 0.08);
  color: #007BFF;
  font-size: .78rem;
  font-weight: 700;
}
.crgm-chip--type { background: rgba(115, 103, 240, 0.12); color: #7367f0; }

.crgm__totals {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: .6rem;
  margin-bottom: 1rem;
}
.crgm-tile {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: .7rem .5rem;
  border-radius: 12px;
  background: rgba(138, 145, 156, 0.08);
  border: 1px solid rgba(138, 145, 156, 0.14);
}
.crgm-tile__value { font-size: 1.25rem; font-weight: 800; line-height: 1.1; }
.crgm-tile__label {
  margin-top: .2rem;
  font-size: .66rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: .04em;
  color: #8a919c;
  text-align: center;
}
.crgm-tile--free { background: rgba(0, 207, 232, 0.08); border-color: rgba(0, 207, 232, 0.25); .crgm-tile__value { color: #00cfe8; } }
.crgm-tile--late { background: rgba(234, 84, 85, 0.06); border-color: rgba(234, 84, 85, 0.22); .crgm-tile__value { color: #ea5455; } }
.crgm-tile--warn { background: rgba(255, 159, 67, 0.08); border-color: rgba(255, 159, 67, 0.3); .crgm-tile__value { color: #ff9f43; } }
.crgm-tile--ok { background: rgba(40, 199, 111, 0.08); border-color: rgba(40, 199, 111, 0.25); .crgm-tile__value { color: #28c76f; } }
.crgm-tile--paid { background: rgba(0, 123, 255, 0.07); border-color: rgba(0, 123, 255, 0.25); .crgm-tile__value { color: #007BFF; } }
.crgm-tile--cost { background: rgba(115, 103, 240, 0.08); border-color: rgba(115, 103, 240, 0.28); .crgm-tile__value { color: #7367f0; } }

/* ===== Payment breakdown (real FTDs + guarantee fill = total) ===== */
.crgm__paysplit {
  display: flex;
  align-items: stretch;
  gap: .5rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
}
.crgm-pay {
  flex: 1 1 0;
  min-width: 150px;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: .6rem .5rem;
  border-radius: 12px;
  border: 1px solid rgba(138, 145, 156, 0.14);
  background: rgba(138, 145, 156, 0.06);
}
.crgm-pay__label {
  font-size: .66rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: .04em;
  color: #8a919c;
  text-align: center;
}
.crgm-pay__math { margin-top: .15rem; font-size: .74rem; color: #8a919c; font-variant-numeric: tabular-nums; }
.crgm-pay__value { margin-top: .1rem; font-size: 1.15rem; font-weight: 800; font-variant-numeric: tabular-nums; }
.crgm-pay--ftd { background: rgba(0, 123, 255, 0.07); border-color: rgba(0, 123, 255, 0.25); .crgm-pay__value { color: #007BFF; } }
.crgm-pay--cg { background: rgba(255, 159, 67, 0.08); border-color: rgba(255, 159, 67, 0.3); .crgm-pay__value { color: #ff9f43; } }
.crgm-pay--total { background: rgba(115, 103, 240, 0.08); border-color: rgba(115, 103, 240, 0.28); .crgm-pay__value { color: #7367f0; } }
.crgm-pay__op {
  align-self: center;
  font-size: 1.1rem;
  font-weight: 800;
  color: #8a919c;
  flex-shrink: 0;
}

.crgm__table-wrap { max-height: 45vh; overflow-y: auto; }
.crgm__payment { font-weight: 800; color: #7367f0; font-variant-numeric: tabular-nums; }
.dark-layout .crgm__payment { color: #b0a6ff; }
.crgm__payment-split { color: #ff9f43; font-weight: 700; white-space: nowrap; }

.crgm__rule {
  margin-top: .9rem;
  padding: .6rem .85rem;
  border-radius: 10px;
  background: rgba(0, 123, 255, 0.06);
  border: 1px dashed rgba(0, 123, 255, 0.35);
  font-size: .78rem;
  color: #5e5873;
}
.crgm__flat {
  padding: 1.2rem .85rem;
  text-align: center;
  color: #6e6b7b;
  font-size: .88rem;
}

@media (max-width: 768px) {
  .crgm__totals { grid-template-columns: repeat(2, 1fr); }
}

.dark-layout {
  .crgm-content { background: #283046; color: #d0d2d6; }
  .crgm__rule { color: #d0d2d6; }
  .crgm__flat { color: #b4b7bd; }
}
</style>
